import type { Outcome } from "@/hooks/firestore/models/outcome";
import type {
  ExoticOutcomesByType,
  ExoticSelectedOutcomes,
  ExoticType,
} from "@/features/race/hooks/useExoticMarket";
import type { Nullable } from "@/types/utilities";
import {
  type ExoticMarketType,
  exoticMarketTypes,
} from "@/features/race/types/exotics";
import type { SubOutcome } from "@/types/BetTypes";

export type ExoticsOutcomeType = Partial<Outcome["type"]>;

export const exoticPositionFromOutcomeMap = new Map<ExoticsOutcomeType, number>(
  [
    ["RUNNER_WIN", 1],
    ["RUNNER_POS_2", 2],
    ["RUNNER_TOP_2", 2],
    ["RUNNER_POS_3", 3],
    ["RUNNER_TOP_3", 3],
    ["RUNNER_POS_4", 4],
    ["RUNNER_TOP_4", 4],
  ],
);

const exoticTypeToOutcomeType: Record<
  ExoticType,
  [ExoticsOutcomeType, Nullable<number>][]
> = {
  TRIFECTA: [
    ["RUNNER_WIN", 1],
    ["RUNNER_POS_2", 2],
    ["RUNNER_POS_3", 3],
  ],
  FIRST_4: [
    ["RUNNER_WIN", 1],
    ["RUNNER_POS_2", 2],
    ["RUNNER_POS_3", 3],
    ["RUNNER_POS_4", 4],
  ],
  EXACTA: [
    ["RUNNER_WIN", 1],
    ["RUNNER_POS_2", 2],
  ],
  QUINELLA: [["RUNNER_WIN", null]],
};

export const byNumericRunnerPosition = <T extends Outcome>(a: T, b: T) => {
  const aPosition = exoticPositionFromOutcomeMap.get(a.type) || 0;
  const bPosition = exoticPositionFromOutcomeMap.get(b.type) || 0;
  return aPosition - bPosition;
};

export const outcomesToOutcomeIds = (
  outcomes?: ExoticOutcomesByType["string"],
) => {
  return (outcomes || []).map((outcome) => outcome.outcomeId);
};

export const getPositionsByExoticType = (type: ExoticType) =>
  exoticTypeToOutcomeType[type];

export const calculateExoticCombinations = (
  selections: ExoticSelectedOutcomes["string"][],
): number => {
  const helper = (
    remainingSelections: ExoticSelectedOutcomes["string"][],
    usedHorses: Set<string>,
  ): number => {
    if (remainingSelections.length === 0) return 1;

    const currentSelection = remainingSelections[0];
    const remaining = remainingSelections.slice(1);
    let count = 0;

    currentSelection.forEach((horse) => {
      if (!usedHorses.has(horse.attributes?.runnerNumber)) {
        const newUsedHorses = new Set(usedHorses);
        newUsedHorses.add(horse.attributes?.runnerNumber);
        count += helper(remaining, newUsedHorses);
      }
    });

    return count;
  };

  return helper(selections, new Set<string>());
};

export const calculateQuinellaCombinationsFromOutcomes = (
  selections: [
    ExoticSelectedOutcomes["string"][],
    ExoticSelectedOutcomes["string"][],
  ],
): number => {
  const [firstPlaceSelections, secondPlaceSelections] = selections;

  const usedPairs = new Set<string>();
  let count = 0;

  (firstPlaceSelections[0] || []).forEach((horse1) => {
    (secondPlaceSelections[0] || []).forEach((horse2) => {
      if (horse1.attributes?.runnerNumber !== horse2.attributes?.runnerNumber) {
        const pair = [
          horse1.attributes?.runnerNumber,
          horse2.attributes?.runnerNumber,
        ]
          .sort()
          .join("-");
        if (!usedPairs.has(pair)) {
          usedPairs.add(pair);
          count++;
        }
      }
    });
  });

  return count;
};

export const isExoticMarketType = (
  marketName: string,
): marketName is ExoticMarketType => {
  if (!marketName) return false;

  return (
    // marketNames are different to marketTypes, so First_4 in some cases
    // needs to drop the underscore, others can simply be uppercased
    marketName === "First4" ||
    exoticMarketTypes.includes(marketName.toUpperCase() as ExoticMarketType)
  );
};

export type ExoticOutcome = {
  attributes: { runnerNumber: string };
  outcomeId?: string;
  outcomeResult: string;
};

export const addToAccumulator =
  <T extends ExoticOutcome>(acc: T[][]) =>
  (pos: number, outcome: T) => {
    if (!acc[pos]) {
      acc[pos] = [];
    }

    if (!acc[pos].some((o) => o.outcomeId === outcome.outcomeId)) {
      acc[pos].push(outcome);
    }
  };

export const groupRunnersByPosition = (
  selectedOutcomes: Record<string, SubOutcome>,
): SubOutcome[][] => {
  return Object.values(selectedOutcomes)
    .sort((a, b) => {
      const aPosition =
        exoticPositionFromOutcomeMap.get(a.type as ExoticsOutcomeType) || 0;
      const bPosition =
        exoticPositionFromOutcomeMap.get(b.type as ExoticsOutcomeType) || 0;
      return aPosition - bPosition;
    })
    .reduce((acc, outcome) => {
      const { type } = outcome;
      const position = exoticPositionFromOutcomeMap.get(
        type as ExoticsOutcomeType,
      );
      const addRunnerToPosition = addToAccumulator(acc);

      if (type === "RUNNER_TOP_2") {
        // this is a quinella edge case
        // top two means both first and second so to need to duplicate the same
        // numbers for first position
        addRunnerToPosition(1, outcome);
      }

      addRunnerToPosition(position || 1, outcome);
      return acc;
    }, []);
};

/** Order isn't considered in quinella combinations */
export const calculateQuinellaCombinations = (
  selections: SubOutcome[][],
): number => {
  const [firstPlaceSelections, secondPlaceSelections] = selections;

  const usedPairs = new Set<string>();
  let count = 0;

  firstPlaceSelections.forEach((horse1) => {
    secondPlaceSelections.forEach((horse2) => {
      if (horse1.attributes.runnerNumber !== horse2.attributes.runnerNumber) {
        const pair = [
          horse1.attributes.runnerNumber,
          horse2.attributes.runnerNumber,
        ]
          .sort()
          .join("-");
        if (!usedPairs.has(pair)) {
          usedPairs.add(pair);
          count++;
        }
      }
    });
  });

  return count;
};
